import React from 'react'

import {
  botAvatarStyles,
  bubbleStyles,
  bubbleWrapperStyles,
  dateStyles,
  messageStyles,
  thumbnailStyles,
} from './styles'
import BotAvatar from './BotAvatar'
import { ChatBubbleProps } from './ChatBubble.types'

/**
 * A single message written from an author and should be included in a chat log.
 *
 * @param {object} author First and last name of author. We turn these values into intitials. If no author is provided, we show the bot avatar instead.
 * @param {any} children Author's content
 * @param {string} className Adds more classes to the wrapper for specific use cases
 * @param {Date} date Optional date to display when the message was written
 * @param {boolean} isMember Defaults to false. Whether the author is the member vs a bot or admin user. If member, the bubble is blue.
 * @param {boolean} showTitle Defaults to false. Whether to show the author name and date above the bubble or not.
 * @returns A single bubble that can be inserted into a chat log
 */
export const ChatBubble = ({
  author,
  children,
  className = '',
  date,
  isMember,
  showTitle = false,
  ...rest
}: ChatBubbleProps) => {
  // Generate initials from firstName and lastName
  const firstNameInitial = author?.firstName?.substring(0, 1).toUpperCase() || ''
  const lastNameInitial = author?.lastName?.substring(0, 1).toUpperCase() || ''
  const authorInitials = firstNameInitial + lastNameInitial

  // Show "ME" if no initials can be generated, otherwise show the initials
  const thumbnailText = authorInitials || 'ME'

  return (
    // Wrapper keeps the thumbnail on the left
    <div className={`${messageStyles} ${className}`} {...rest}>
      {/* If the author has a name, show a thumbnail with their initials
        Otherwise, show the bot avatar */}
      {author ? (
        <div className={thumbnailStyles(isMember)}>
          {author.image ? (
            <img
              src={author.image}
              alt={`${author?.firstName} ${author?.lastName}`}
              className="w-full"
            />
          ) : (
            <>
              {thumbnailText}
            </>
          )}
        </div>
      ) : (
        <BotAvatar className={botAvatarStyles} />
      )}

      <div className={bubbleWrapperStyles({ showTitle })}>
        {showTitle && (
          <div className="mb-1">
            <span className="sh-strong">
              {author?.firstName} {author?.lastName}
            </span>

            {date && (
              <time
                dateTime={date.toLocaleString()}
                className="sh-body-xs ml-1"
              >
                {date.toLocaleString('en-us', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                })}
              </time>
            )}
          </div>
        )}

        <div className={bubbleStyles(isMember)}>{children}</div>

        {date && !showTitle && (
          <time className={dateStyles} dateTime={date.toLocaleString()}>
            {date.toLocaleString('en-us', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            })}
          </time>
        )}
      </div>
    </div>
  )
}

